/*
 * Longeva Aesthetics - PDP buy-box enhancer
 *
 * Mounts the senior-spec buy-box chrome onto Horizon's product-information
 * section at runtime. Cannot be done in Liquid because the buy-box blocks
 * are sealed _product-* types in Horizon shipped sections.
 *
 * Boot order:
 *   1. Read JSON data island (#longeva-pdp-data).
 *   2. Wait for DOMContentLoaded.
 *   3. Mount chips + provenance above title.
 *   4. Mount VAT note below price.
 *   5. Mount cold-chain badge above variant picker (if cold-chain).
 *   6. Mount volume-pricing band above variant picker (if multi-variant).
 *   7. Mount dispatch cutoff line above add-to-cart.
 *   8. Mount trust-signal strip + disclaimer below buy-buttons.
 *   9. Collapse accelerated checkout into expander.
 *  10. Mount stock/availability signal between provenance and price.
 *  11. Mount sticky mobile add-to-basket bar.
 */

(() => {
  "use strict";

  const dataEl = document.getElementById("longeva-pdp-data");
  if (!dataEl) return;

  // CRITICAL: Horizon does NOT add `.template-product` to <body>. All scoped
  // `body.template-product .*` rules in longeva-atmosphere.css §12/§17 only
  // activate if we add this class ourselves. Add as early as possible so the
  // first paint has the brand styles.
  if (!document.body.classList.contains("template-product")) {
    document.body.classList.add("template-product");
  }

  let data;
  try {
    data = JSON.parse(dataEl.textContent);
  } catch (_) {
    return;
  }

  const ready = (fn) => {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", fn, { once: true });
    } else {
      fn();
    }
  };

  const $ = (sel, root = document) => root.querySelector(sel);

  // ───────────────────────── helpers ─────────────────────────
  const make = (tag, attrs = {}, ...children) => {
    const el = document.createElement(tag);
    Object.entries(attrs).forEach(([k, v]) => {
      if (k === "class") el.className = v;
      else if (k === "html") el.innerHTML = v;
      else if (k.startsWith("on") && typeof v === "function") {
        el.addEventListener(k.slice(2).toLowerCase(), v);
      } else if (v === false || v == null) {
        // skip
      } else {
        el.setAttribute(k, v === true ? "" : String(v));
      }
    });
    children.flat().forEach((c) => {
      if (c == null) return;
      el.append(typeof c === "string" ? document.createTextNode(c) : c);
    });
    return el;
  };

  const dispatchCopy = () => {
    const now = new Date();
    const day = now.getDay(); // 0 Sun, 6 Sat
    const hour = now.getHours();
    const beforeCutoff = hour < (data.dispatch.cutoff_hour || 15);
    if (day === 0 || day === 6) return data.dispatch.label_weekend;
    if (beforeCutoff) return data.dispatch.label_before_cutoff;
    if (day === 5) return data.dispatch.label_after_cutoff_friday;
    return data.dispatch.label_after_cutoff_weekday;
  };

  // ───────────────────────── 1. brand overline ─────────────────────────
  // M&S discipline: a single quiet brand label above the title. No chip
  // stack, no indication chip, no SKU provenance line (SKU stays in
  // schema/JSON-LD; brand/indication detail lives in the accordions).
  const mountBrandOverline = () => {
    if (!data.brand.name) return;
    const titleEl =
      $(".product-title") ||
      $("[class*='product-title']") ||
      $(".product-information h1") ||
      $("h1");
    if (!titleEl) return;
    if (titleEl.previousElementSibling?.classList?.contains("longeva-pdp-chips")) return;

    const wrap = make("div", { class: "longeva-pdp-chips", "aria-label": "Brand" });
    const brandHref = data.brand.collection_url
      || (data.brand.handle ? `/pages/longeva-brand/${data.brand.handle}` : null);
    const brandEl = brandHref
      ? make("a", { class: "longeva-pdp-chip longeva-pdp-chip--brand", href: brandHref }, data.brand.name)
      : make("span", { class: "longeva-pdp-chip longeva-pdp-chip--brand" }, data.brand.name);
    wrap.append(brandEl);
    titleEl.before(wrap);
  };

  // ───────────────────────── 2. VAT note below price ─────────────────────────
  const mountVatNote = () => {
    const priceEl =
      $(".product-information .price-container") ||
      $(".product-information [class*='price']") ||
      $(".product-information .price");
    if (!priceEl) return;
    const container = priceEl.closest("[class*='price-container']") || priceEl.parentElement;
    if (!container) return;
    if (container.querySelector(".longeva-pdp-vat-note")) return;
    const vat = make("span", { class: "longeva-pdp-vat-note" }, "Price excludes VAT. VAT added at checkout.");
    container.append(vat);
  };

  // ───────────────────────── 3. cold-chain ─────────────────────────
  const mountColdChain = () => {
    if (!data.cold_chain) return;
    const variantPicker = $("variant-picker") || $(".variant-picker");
    if (!variantPicker) return;
    if (variantPicker.previousElementSibling?.classList?.contains("longeva-pdp-coldchain")) return;
    const svg =
      `<svg class="longeva-pdp-coldchain__icon" viewBox="0 0 16 16" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M8 2v9"/><path d="M5 8h6"/><circle cx="8" cy="12.5" r="2"/><path d="M8 4l-2-1.5M8 4l2-1.5"/></svg>`;
    const el = make("div", { class: "longeva-pdp-coldchain", html: svg });
    el.append(document.createTextNode("Cold-chain dispatch. Validated 2-8 °C transit."));
    variantPicker.before(el);
  };

  // ───────────────────────── 4. volume-pricing band ─────────────────────────
  const mountVolumeBand = () => {
    if (!data.multi_variant) return;
    const buyButtons = $(".product-form-buttons") || $("[class*='product-form-buttons']");
    if (!buyButtons) return;
    const host = buyButtons.parentElement;
    if (!host || host.querySelector(".longeva-pdp-volume")) return;
    const band = make(
      "div",
      { class: "longeva-pdp-volume", role: "note" },
      make("span", { class: "longeva-pdp-volume__label" }, "Trade saving"),
      make(
        "span",
        { class: "longeva-pdp-volume__value" },
        "Box quantities lower the unit cost. Select the box variant for the wholesale per-unit price."
      )
    );
    // CRO: explanatory note belongs BELOW the buy action, not between price
    // and ATC (it pushed the ATC off the first fold on mobile).
    buyButtons.after(band);
  };

  // ───────────────────────── 5. dispatch cutoff ─────────────────────────
  const mountDispatch = () => {
    const buyButtons = $(".product-form-buttons") || $("[class*='product-form-buttons']");
    if (!buyButtons) return;
    const host = buyButtons.parentElement;
    if (!host || host.querySelector(".longeva-pdp-dispatch")) return;
    const el = make("p", { class: "longeva-pdp-dispatch" }, dispatchCopy());
    // CRO: dispatch reassurance sits just under the ATC, not above it.
    buyButtons.after(el);
  };

  // ───────────────────────── 6. single quiet trust line ─────────────────────────
  // One muted sourcing line below the buy action. The practitioner
  // disclaimer is NOT injected here - it lives in the Regulatory
  // accordion (longeva-pdp-details). One place, no buy-box clutter.
  const mountTrust = () => {
    const buyButtons = $(".product-form-buttons") || $("[class*='product-form-buttons']");
    if (!buyButtons) return;
    const host = buyButtons.parentElement;
    if (!host) return;
    if (host.querySelector(".longeva-pdp-trust-strip")) return;

    const trust = make("ul", { class: "longeva-pdp-trust-strip", "aria-label": "Sourcing" });
    trust.append(make("li", {}, "Sourced from UK-licensed manufacturers only"));
    buyButtons.after(trust);
  };

  // ───────────────────────── 7. accelerated checkout → expander ─────────────────────────
  const mountAccelExpander = () => {
    const buyButtons = $(".product-form-buttons");
    if (!buyButtons) return;
    const accel =
      buyButtons.querySelector(".accelerated-checkout-block") ||
      buyButtons.querySelector("[class*='accelerated-checkout']") ||
      buyButtons.querySelector(".shopify-payment-button");
    if (!accel) {
      buyButtons.removeAttribute("data-longeva-accel-hidden");
      return;
    }
    if (accel.dataset.longevaWrapped === "1") {
      buyButtons.removeAttribute("data-longeva-accel-hidden");
      return;
    }
    accel.dataset.longevaWrapped = "1";

    const panel = make("div", {
      class: "longeva-pdp-accel-panel",
      id: "longeva-pdp-accel-panel",
      hidden: true,
    });
    accel.before(panel);
    panel.append(accel);

    const chevron = make("span", { class: "longeva-pdp-accel-toggle__chevron", "aria-hidden": "true" }, "▾");
    const toggle = make(
      "button",
      {
        type: "button",
        class: "longeva-pdp-accel-toggle",
        "aria-expanded": "false",
        "aria-controls": "longeva-pdp-accel-panel",
        onClick: (e) => {
          const open = toggle.getAttribute("aria-expanded") === "true";
          toggle.setAttribute("aria-expanded", String(!open));
          if (open) panel.setAttribute("hidden", "");
          else panel.removeAttribute("hidden");
        },
      },
      "More ways to pay",
      chevron
    );
    panel.before(toggle);

    buyButtons.removeAttribute("data-longeva-accel-hidden");
  };

  // ───────────────────────── 8. stock/availability signal ─────────────────────────
  const mountStockSignal = () => {
    // Bail if signal already mounted.
    if ($(".longeva-pdp-stock")) return;

    const inv = data.inventory;
    if (!inv) return;

    // Determine state. Thresholds:
    //   out  - variant.available is false
    //   low  - tracked, available, quantity < 10
    //   in   - tracked, available, quantity >= 10 (or untracked/continue policy)
    let state;
    let label;

    if (!inv.available) {
      state = "out";
      label = "Made to order";
    } else if (inv.management === "shopify" && inv.policy !== "continue" && typeof inv.quantity === "number" && inv.quantity < 10) {
      state = "low";
      label = inv.quantity <= 0 ? "Low availability" : `${inv.quantity} remaining`;
    } else {
      state = "in";
      label = "In stock";
    }

    const signal = make("p", {
      class: "longeva-pdp-stock",
      "data-stock": state,
      role: "status",
      "aria-live": "polite",
      "aria-label": `Stock status: ${label}`,
    },
      make("span", { class: "longeva-pdp-stock__label" }, label)
    );

    // Insert after the provenance line (below product title + SKU line) or
    // before the price block - whichever node comes first.
    const provenanceLine = $(".longeva-pdp-provenance");
    const priceEl = $(".product-information product-price") || $(".product-information .price-container") || $(".product-information .price");

    if (provenanceLine) {
      provenanceLine.after(signal);
    } else if (priceEl) {
      priceEl.before(signal);
    } else {
      // Fallback: inside the buy-box container
      const buyBox = $(".product-information .product-details-block") || $(".product-information .product-form");
      if (buyBox) buyBox.prepend(signal);
    }

    // Trigger entry animation after one frame so transition fires.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        signal.setAttribute("data-ready", "");
      });
    });

    // Re-mount if variant changes. Listen to Horizon's variant update event.
    const onVariantChange = (e) => {
      const existing = $(".longeva-pdp-stock");
      if (existing) existing.remove();
      // Recalculate based on the newly selected variant from Horizon's event detail
      // Horizon fires 'variant:updated' or updates the data island directly.
      // We let the next runAll cycle handle re-mount from fresh DOM state.
    };
    document.addEventListener("variant:updated", onVariantChange, { passive: true });
  };

  // ───────────────────────── 9. sticky mobile add-to-basket ─────────────────────────
  const mountStickyMobile = () => {
    if (window.matchMedia("(min-width: 750px)").matches) return;
    if ($(".longeva-pdp-sticky")) return;

    const bar = make(
      "div",
      { class: "longeva-pdp-sticky", role: "region", "aria-label": "Add to basket" },
      make(
        "div",
        { class: "longeva-pdp-sticky__meta" },
        make("span", { class: "longeva-pdp-sticky__title" }, data.title),
        make("span", { class: "longeva-pdp-sticky__price" }, data.current_price_money)
      ),
      make(
        "button",
        {
          type: "button",
          class: "longeva-pdp-sticky__cta",
          onClick: () => {
            const atc = document.querySelector(".add-to-cart-button, [data-testid='add-to-cart-button'], button[name='add']");
            if (atc) atc.click();
          },
        },
        "Add to basket"
      )
    );

    // Show immediately on mobile. The bar is visible by default (CSS §7.6
    // sets transform: translateY(0) on mobile). JS will hide it once the
    // native ATC button scrolls into view. This ensures price + buy action
    // are available above the fold from the first paint.
    bar.setAttribute("data-visible", "true");
    document.body.append(bar);

    // Watch the native ATC button. Show our bar when it leaves the viewport
    // (user has scrolled past it or hasn't reached it yet), hide when it
    // enters the viewport so the two do not compete.
    const observed = document.querySelector(".add-to-cart-button, [data-testid='add-to-cart-button'], button[name='add']");
    if (!observed) return;
    const io = new IntersectionObserver(
      (entries) => {
        const e = entries[0];
        // isIntersecting === true means native ATC is visible: hide our bar.
        // isIntersecting === false means it is off-screen: show our bar.
        bar.setAttribute("data-visible", String(!e.isIntersecting));
      },
      // Use the full viewport root (no margin), threshold 0 so any pixel
      // of the button being visible triggers the callback.
      { rootMargin: "0px", threshold: 0 }
    );
    io.observe(observed);
  };

  // ───────── Open the cart drawer on a PDP add (M&S pattern) ─────────
  // Horizon does not auto-open the drawer on add in this config. On a
  // PDP-initiated add we call Horizon's own cart-drawer-component.open()
  // so the drawer pops (styled as a bottom sheet on mobile / centred
  // modal on desktop by atmosphere §14.13) and the existing AOV
  // cross-sell injects into it. Non-intercepting: the native add is
  // never touched; this only reacts to Horizon's post-add cart events.
  let lvAddIntent = false;
  const mountCartOpenOnAdd = () => {
    const form =
      document.querySelector('.product-information form[action*="/cart/add"]') ||
      document.querySelector("product-form-component form");
    const btn = form
      ? form.querySelector(
          ".button.add-to-cart-button:not(.quick-add__button), button[name='add']"
        )
      : null;
    if (!btn || btn.dataset.lvOpen === "1") return;
    btn.dataset.lvOpen = "1";

    btn.addEventListener(
      "click",
      () => {
        lvAddIntent = true;
        setTimeout(() => {
          lvAddIntent = false;
        }, 6000);
      },
      { passive: true }
    );

    let opening = false;
    const openDrawer = () => {
      if (!lvAddIntent || opening) return;
      lvAddIntent = false;
      opening = true;
      try {
        const cdc = document.querySelector("cart-drawer-component");
        if (cdc && typeof cdc.open === "function") cdc.open();
      } catch (_) {}
      setTimeout(() => {
        opening = false;
      }, 400);
    };
    ["cart:update", "cart:added", "cart:change", "cart:refresh"].forEach((ev) =>
      document.addEventListener(ev, openDrawer, { passive: true })
    );
  };

  // ───────── PDP gallery: quiet chevrons + slim progress bar ─────────
  // Drives Horizon's slideshow-component (.next()/.previous()). Injects a
  // prev/next control and a progress bar that glides to the active
  // segment. Read-only on Horizon state; fully guarded; idempotent.
  const mountGallery = () => {
    const media =
      $(".product-information__media") || $("product-media-gallery");
    if (!media || media.dataset.lvGallery === "1") return;
    const sc = document.querySelector("slideshow-component");
    if (!sc || typeof sc.next !== "function") return;

    const ctrls = () =>
      [...document.querySelectorAll(".slideshow-controls button[aria-label]")];
    const numCtrls = () =>
      ctrls().filter((b) => /^\s*\d+\s*$/.test(b.textContent || ""));
    // Slide count: the Liquid data island is authoritative (controls are
    // hidden on mobile, so do not depend on them). Fallbacks follow.
    let total = parseInt(data && data.media_count, 10) || 0;
    if (!total) {
      const labelled = ctrls()
        .map((b) => (b.getAttribute("aria-label") || "").match(/of\s+(\d+)/i))
        .find(Boolean);
      if (labelled) total = parseInt(labelled[1], 10);
    }
    if (!total) total = numCtrls().length;
    if (!total) {
      const uniq = new Set(
        [
          ...media.querySelectorAll(
            "slideshow-slide[id], slideshow-slide[aria-hidden]"
          ),
        ].map((s) => s.id || s.getAttribute("aria-label") || "")
      );
      total = uniq.size;
    }
    if (!total || total < 2) return;
    media.dataset.lvGallery = "1";

    const bar = make(
      "div",
      { class: "longeva-gallery-bar", "aria-hidden": "true" },
      make("span", { class: "longeva-gallery-bar__fill" })
    );
    bar.style.setProperty("--lv-g-total", String(total));
    bar.style.setProperty("--lv-g-cur", "0");

    const mkNav = (dir) =>
      make("button", {
        type: "button",
        class: "longeva-gallery-nav longeva-gallery-nav--" + dir,
        "aria-label": dir === "prev" ? "Previous image" : "Next image",
        onClick: () => {
          try {
            dir === "prev" ? sc.previous() : sc.next();
          } catch (_) {}
          setTimeout(sync, 60);
        },
      });

    media.appendChild(mkNav("prev"));
    media.appendChild(mkNav("next"));
    media.appendChild(bar);

    const curIndex = () => {
      const nums = numCtrls();
      const i = nums.findIndex(
        (b) =>
          b.getAttribute("aria-current") === "true" ||
          b.getAttribute("aria-selected") === "true" ||
          b.classList.contains("is-active") ||
          b.classList.contains("slideshow-control--active")
      );
      if (i !== -1) return i;
      const slides = [
        ...document.querySelectorAll(
          ".product-media-gallery .product-media[aria-hidden], .product-information__media slideshow-slide[aria-hidden]"
        ),
      ];
      const v = slides.findIndex(
        (s) => s.getAttribute("aria-hidden") === "false"
      );
      return v === -1 ? 0 : Math.min(v, total - 1);
    };
    const sync = () => {
      const idx = Math.max(0, Math.min(curIndex(), total - 1));
      bar.style.setProperty("--lv-g-cur", String(idx));
    };

    // Recompute on any native control interaction.
    const cc = document.querySelector(".slideshow-controls");
    if (cc) {
      cc.addEventListener("click", () => setTimeout(sync, 60), {
        passive: true,
      });
      // Read-only attribute observer; only writes a CSS var on `bar`
      // (a separate element it does not observe) so it cannot loop.
      try {
        const mo = new MutationObserver(() => sync());
        mo.observe(cc, {
          subtree: true,
          attributes: true,
          attributeFilter: ["aria-current", "aria-selected", "class"],
        });
      } catch (_) {}
    }
    document.addEventListener("slideshow:change", sync, { passive: true });
    sync();
  };

  // ───────────────────────── 10. recommendations mobile auto-scroll ─────────────────────────
  const mountRecsAutoScroll = () => {
    if (window.matchMedia("(min-width: 640px)").matches) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const rail = document.querySelector(".product-recommendations .resource-list--grid");
    if (!rail || rail.dataset.longevaAuto === "1") return;
    const items = rail.querySelectorAll(".resource-list__item");
    if (items.length < 2) return;
    rail.dataset.longevaAuto = "1";

    let paused = false;
    let resumeT = null;
    const step = () => {
      const card = rail.querySelector(".resource-list__item");
      const w = card ? card.getBoundingClientRect().width + 14 : rail.clientWidth * 0.7;
      const atEnd = rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 4;
      rail.scrollTo({ left: atEnd ? 0 : rail.scrollLeft + w, behavior: "smooth" });
    };
    const tick = setInterval(() => { if (!paused) step(); }, 3600);

    const pause = () => {
      paused = true;
      if (resumeT) clearTimeout(resumeT);
      resumeT = setTimeout(() => { paused = false; }, 6000);
    };
    ["touchstart", "pointerdown", "wheel"].forEach((ev) =>
      rail.addEventListener(ev, pause, { passive: true })
    );
    // Stop entirely if the section scrolls out of view (save cycles).
    const vis = new IntersectionObserver(
      (e) => { paused = !e[0].isIntersecting ? true : paused; },
      { threshold: 0 }
    );
    vis.observe(rail);
    rail.addEventListener("longeva:destroy", () => clearInterval(tick), { once: true });
  };

  // ───────────────────────── boot ─────────────────────────
  // Native accelerated checkout (Shop Pay etc.) shows normally below the
  // ink ATC - no bespoke expander, no hiding. The ink ATC stays dominant
  // by weight, not by suppressing standard express options.

  ready(() => {
    // Some Horizon blocks render asynchronously via <template> hydration.
    // Run our mounts once now, then re-run after a microtask + a 250ms tick
    // to catch any late-mounted nodes. Idempotent - each mount checks for
    // its own marker before running.
    const runAll = () => {
      try { mountBrandOverline(); } catch (_) {}
      try { mountVatNote(); } catch (_) {}
      try { mountStockSignal(); } catch (_) {}
      try { mountDispatch(); } catch (_) {}
      try { mountTrust(); } catch (_) {}
      try { mountRecsAutoScroll(); } catch (_) {}
      // Reduced to the M&S-discipline essentials. Intentionally NOT
      // mounted (clutter / competing treatments): volume band,
      // cold-chain badge, accelerated-checkout expander. Cold-chain and
      // trade detail live in the accordions; native express checkout
      // shows normally. Sticky bar: Horizon's own native bar is used
      // (brand-styled minimally in longeva-pdp.css).
    };
    runAll();
    queueMicrotask(runAll);
    setTimeout(runAll, 250);
    setTimeout(runAll, 1200);
  });
})();
